import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useBroker } from '../../contexts/BrokerContext';
import { Sun, Moon, Globe, LogOut, Zap, ArrowUpRight, Menu, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Header({ onMenuClick, sidebarOpen }) {
  const { admin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { locale, changeLocale, t } = useLocale();
  const { dashboardStats } = useBroker();
  const navigate = useNavigate();

  const plan    = dashboardStats?.plan    || null;
  const upgrade = dashboardStats?.upgrade || null;

  return (
    <header className="h-16 bg-[var(--bg-secondary)] border-b border-[var(--border-color)] px-4 flex items-center justify-between">

      {/* ── Left ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">

        {/* Sidebar toggle — works on both mobile and desktop */}
        <button
          onClick={onMenuClick}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-smooth"
          title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
        >
          {sidebarOpen
            ? <X    className="w-5 h-5 text-[var(--text-secondary)]" />
            : <Menu className="w-5 h-5 text-[var(--text-secondary)]" />}
        </button>

        {/* Welcome + plan */}
        <div className="flex items-center gap-3">
          <p className="text-sm text-[var(--text-secondary)] hidden sm:block">
            {t('common.welcome')},{' '}
            <span className="font-semibold text-[var(--text-primary)]">{admin?.firstName}</span>
          </p>

          {plan?.name && (
            <div className="flex items-center gap-2">
              <span
                className="text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--accent-primary)18',
                  color:           'var(--accent-primary)',
                  border:          '1px solid var(--accent-primary)30',
                }}
              >
                {plan.name}
              </span>

              {upgrade && (
                <button
                onClick={() => navigate('/upgrade')}
                  className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-0.5 rounded-full transition-all hover:opacity-85 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-info))', color: '#fff' }}
                  title={`Upgrade to ${upgrade.name} — $${upgrade.price}/mo`}
                >
                  <Zap className="w-3 h-3" />
                  {upgrade.name}
                  <ArrowUpRight className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Right ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">

        {/* Language */}
        <button
          onClick={() => changeLocale(locale === 'en' ? 'ar' : 'en')}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-smooth"
          title={t('language.select')}
        >
          <Globe className="w-5 h-5" />
        </button>

        {/* Theme */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] transition-smooth"
          title={t('theme.toggle')}
        >
          {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* User + logout */}
        <div className="flex items-center gap-3 pl-3 border-l border-[var(--border-color)]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[var(--accent-primary)] flex items-center justify-center text-white font-semibold text-sm">
              {admin?.firstName?.[0]}
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium">{admin?.firstName} {admin?.lastName}</p>
              <p className="text-xs text-[var(--text-secondary)]">{admin?.role}</p>
            </div>
          </div>

          <button
            onClick={logout}
            className="p-2 rounded-lg hover:bg-[var(--bg-tertiary)] text-[var(--accent-danger)] transition-smooth"
            title={t('auth.logout')}
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

    </header>
  );
}